const BLOCK_ELEMENTS = new Set([
    'address',
    'article',
    'aside',
    'div',
    'footer',
    'header',
    'main',
    'nav',
    'section',
]);

function escapeMarkdownText(value) {
    return String(value || '').replace(/([\\`*_[\]<>])/g, '\\$1');
}

function escapeHtmlAttribute(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function serializeChildren(element, context) {
    return Array.from(element.childNodes)
        .map((node) => serializeNode(node, context))
        .join('');
}

function serializeList(element, context) {
    const ordered = element.tagName.toLowerCase() === 'ol';
    const start = Number(element.getAttribute('start')) || 1;

    return Array.from(element.children)
        .filter((child) => child.tagName?.toLowerCase() === 'li')
        .map((item, index) => {
            const marker = ordered ? `${start + index}. ` : '- ';
            const body = serializeChildren(item, {
                ...context,
                listDepth: (context.listDepth || 0) + 1,
            })
                .trim()
                .replace(/\n+/g, '\n');
            const indent = '  '.repeat(context.listDepth || 0);
            return `${indent}${marker}${body.replace(/\n/g, `\n${indent}  `)}`;
        })
        .join('\n');
}

function serializeNode(node, context = {}) {
    if (node.nodeType === node.TEXT_NODE) {
        return context.escapeText === false
            ? String(node.nodeValue || '')
            : escapeMarkdownText(node.nodeValue);
    }

    if (node.nodeType !== node.ELEMENT_NODE) return '';

    const tag = node.tagName.toLowerCase();
    const children = () => serializeChildren(node, context);

    if (tag === 'br') return '\n';
    if (tag === 'strong' || tag === 'b') return `**${children()}**`;
    if (tag === 'em' || tag === 'i') return `*${children()}*`;
    if (tag === 'del' || tag === 's' || tag === 'strike') {
        return `~~${children()}~~`;
    }
    if (tag === 'code') return `\`${children()}\``;
    if (tag === 'u') return `<u>${children()}</u>`;

    if (tag === 'a') {
        const href = node.getAttribute('href') || '';
        const title = node.getAttribute('title');
        const suffix = title ? ` "${title.replace(/"/g, '\\"')}"` : '';
        return `[${children()}](${href}${suffix})`;
    }

    if (tag === 'span') {
        const style = node.getAttribute('style');
        return style
            ? `<span style="${escapeHtmlAttribute(style)}">${children()}</span>`
            : children();
    }

    if (tag === 'ul' || tag === 'ol') {
        return `${serializeList(node, context)}\n\n`;
    }
    if (tag === 'li') return children();

    if (/^h[1-6]$/.test(tag)) {
        return `${'#'.repeat(Number(tag[1]))} ${children().trim()}\n\n`;
    }
    if (tag === 'p') return `${children().trim()}\n\n`;
    if (tag === 'blockquote') {
        return `${children()
            .trim()
            .split('\n')
            .map((line) => `> ${line}`)
            .join('\n')}\n\n`;
    }
    if (BLOCK_ELEMENTS.has(tag)) return `${children()}\n`;

    // Browsers can inject harmless editing-only wrappers. Their content is
    // retained without persisting browser-specific HTML into markdown fields.
    return children();
}

/**
 * Convert the rendered contents of the on-page contentEditable back to the
 * markdown format expected by the drawer editor and content JSON files.
 */
export function editableHtmlToMarkdown(root, options = {}) {
    if (!root) return '';

    return serializeChildren(root, {
        listDepth: 0,
        escapeText: options.escapeText !== false,
    })
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Repair values saved by the original on-page editor, which persisted the
 * rendered ReactMarkdown HTML instead of the field's markdown source.
 * Markdown markers inside those wrappers must remain unescaped so MDXEditor
 * can interpret formatting such as **bold** and *italic*.
 */
export function legacyInlineHtmlToMarkdown(value) {
    const input = String(value ?? '');
    if (!/^\s*<(?:p|h[1-6]|ul|ol|blockquote|div)\b/i.test(input)) {
        return input;
    }

    if (typeof document === 'undefined') return input;

    const template = document.createElement('template');
    template.innerHTML = input;
    return editableHtmlToMarkdown(template.content, { escapeText: false });
}
